import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fundAPI } from '../services/api';
import axios from 'axios';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FundForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    fund_currency: 'USD',
    fund_suffix: '',
    date_of_first_close: '',
    date_of_final_close: '',
    target_size: '',
    actual_corpus: '',
    sebi_aif_category: '',
    sebi_registration_number: '',
    corpus_at_first_closing: '',
    corpus_at_final_closing: '',
    management_fee_percentage: '',
    setup_fee_percentage: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Track the originally-loaded suffix so we know if it was changed
  const originalSuffixRef = useRef('');
  // Confirmation dialog state for cascading suffix change
  const [cascadePreview, setCascadePreview] = useState(null); // { data, payload }

  useEffect(() => {
    if (isEditing) {
      fetchFund();
    }
  }, [id]);

  const fetchFund = async () => {
    try {
      const response = await fundAPI.getById(id);
      setFormData(response.data);
      originalSuffixRef.current = (response.data?.fund_suffix || '').trim();
    } catch (err) {
      setError('Failed to load fund');
    }
  };

  const sanitisePayload = () => Object.fromEntries(
    Object.entries(formData).map(([key, value]) => {
      // `fund_suffix` is intentionally clearable — keep it as an empty string
      // so the backend can detect the "user wants to remove the suffix"
      // intent. (For everything else, null is fine because Pydantic models
      // accept Optional[T].) Without this carve-out, the v-is-not-None filter
      // on the backend strips the field out and the cascade never runs.
      if (key === 'fund_suffix') return [key, value ?? ''];
      return [key, value === '' ? null : value];
    })
  );

  const submitNow = async (data) => {
    setLoading(true);
    setError('');
    try {
      if (isEditing) {
        const res = await fundAPI.update(id, data);
        const cascade = res?.data?.cascade;
        if (cascade && (cascade.account_investments_updated || cascade.drawdown_investors_updated)) {
          // Soft success notification
          window.alert(`Fund saved. Folio numbers updated across ${cascade.account_investments_updated} investment(s) and ${cascade.drawdown_investors_updated} drawdown investor record(s).`);
        }
      } else {
        await fundAPI.create(data);
      }
      navigate('/funds');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save fund');
    } finally {
      setLoading(false);
      setCascadePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = sanitisePayload();
    const newSuffix = (data.fund_suffix || '').trim();
    const suffixChanged = isEditing && newSuffix !== originalSuffixRef.current;

    if (suffixChanged) {
      // Fetch cascade preview to render a confirmation dialog
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_URL}/funds/${id}/folio-cascade-preview`,
          { headers: { Authorization: `Bearer ${token}` }, params: { new_suffix: newSuffix } },
        );
        const affected = (res.data?.account_investments_affected || 0) + (res.data?.drawdown_investors_affected || 0);
        if (affected > 0) {
          setCascadePreview({ data: res.data, payload: data });
          return;
        }
      } catch (err) {
        // If preview fails, fall through to direct submit (backend will still cascade if needed)
        console.warn('Cascade preview failed, proceeding to submit:', err);
      }
    }
    await submitNow(data);
  };

  const confirmCascade = async () => {
    if (cascadePreview?.payload) {
      await submitNow(cascadePreview.payload);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <button
        onClick={() => navigate('/funds')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Funds
      </button>

      <div className="bg-white rounded-xl shadow-md p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditing ? 'Edit Fund' : 'Create New Fund'}
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fund Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="fund-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fund Currency <span className="text-red-500">*</span>
              </label>
              <select
                name="fund_currency"
                required
                value={formData.fund_currency}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SEBI AIF Category
              </label>
              <select
                name="sebi_aif_category"
                value={formData.sebi_aif_category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Category</option>
                <option value="Category I">Category I</option>
                <option value="Category II">Category II</option>
                <option value="Category III">Category III</option>
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of First Close
              </label>
              <input
                type="date"
                name="date_of_first_close"
                value={formData.date_of_first_close || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Final Close
              </label>
              <input
                type="date"
                name="date_of_final_close"
                value={formData.date_of_final_close || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Financial Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Size
              </label>
              <input
                type="number"
                name="target_size"
                step="0.01"
                value={formData.target_size || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actual Corpus
              </label>
              <input
                type="number"
                name="actual_corpus"
                step="0.01"
                value={formData.actual_corpus || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Corpus at First Closing
              </label>
              <input
                type="number"
                name="corpus_at_first_closing"
                step="0.01"
                value={formData.corpus_at_first_closing || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Corpus at Final Closing
              </label>
              <input
                type="number"
                name="corpus_at_final_closing"
                step="0.01"
                value={formData.corpus_at_final_closing || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Registration & Fees */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SEBI Registration Number
              </label>
              <input
                type="text"
                name="sebi_registration_number"
                value={formData.sebi_registration_number || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fund Suffix
              </label>
              <input
                type="text"
                name="fund_suffix"
                value={formData.fund_suffix || ''}
                onChange={handleChange}
                placeholder="e.g., FUND1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="fund-suffix-input"
              />
              <p className="text-xs text-gray-400 mt-1">Used to auto-generate Folio No. as Fund Suffix-PPM No.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Management Fee (%)
              </label>
              <input
                type="number"
                name="management_fee_percentage"
                step="0.01"
                value={formData.management_fee_percentage || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Setup Fee (%)
              </label>
              <input
                type="number"
                name="setup_fee_percentage"
                step="0.01"
                value={formData.setup_fee_percentage || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/funds')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
              data-testid="save-fund-button"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Saving...' : 'Save Fund'}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation dialog — Fund Suffix cascade */}
      {cascadePreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="suffix-cascade-dialog">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex items-center gap-2.5">
              <AlertTriangle className="text-amber-500" size={22} />
              <h2 className="text-lg font-semibold text-gray-900">Cascade Fund Suffix Change?</h2>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
              <p>
                Changing the Fund Suffix from <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">{cascadePreview.data.current_suffix || '(empty)'}</code> to <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono">{cascadePreview.data.new_suffix}</code> will update the cached folio numbers on:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                <li><strong>{cascadePreview.data.account_investments_affected}</strong> account investment(s)</li>
                <li><strong>{cascadePreview.data.drawdown_investors_affected}</strong> drawdown investor record(s)</li>
              </ul>
              {cascadePreview.data.sample_changes && cascadePreview.data.sample_changes.length > 0 && (
                <div className="border rounded-lg bg-gray-50 p-3 text-xs font-mono">
                  <p className="text-gray-500 mb-1.5 font-sans not-italic">Sample changes:</p>
                  {cascadePreview.data.sample_changes.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-gray-500 line-through">{s.old}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-emerald-700 font-semibold">{s.new}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">
                PPM numbers remain unchanged. Only the suffix portion of each Folio No. is rewritten. This action will be recorded in the audit log.
              </p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCascadePreview(null)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="suffix-cascade-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmCascade}
                disabled={loading}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                data-testid="suffix-cascade-confirm"
              >
                {loading ? 'Updating…' : 'Yes, update all folios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundForm;
